import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { apiPost, apiPut } from "../../utils/api";
import { useLanguage } from "../../contexts/LanguageContext";
import { AdminFormDialog, AdminField } from "./AdminFormDialog";
import { Input } from "../ui/input";

export type KeyFactFormState = {
  title: string;
  titleEn: string;
  value: string;
  sortOrder: string;
  isActive: boolean;
};

export const EMPTY_KEY_FACT: KeyFactFormState = {
  title: "", titleEn: "", value: "", sortOrder: "", isActive: true,
};

type Props = {
  open: boolean;
  editing: any | null;
  onClose: () => void;
  onSaved: () => void;
};

const FORM_ID = "keyfact-form";

export const AdminKeyFactModal: React.FC<Props> = ({ open, editing, onClose, onSaved }) => {
  const { t } = useLanguage();
  const [localeTab, setLocaleTab] = useState<"fr" | "en">("fr");

  const schema = Yup.object({
    title: Yup.string().trim().required(t.common.required),
    value: Yup.string().trim().required(t.common.required),
    titleEn: Yup.string(),
    sortOrder: Yup.number().typeError("Nombre invalide").min(0).nullable().transform((v, o) => (o === "" ? null : v)),
    isActive: Yup.boolean(),
  });

  const initial: KeyFactFormState = editing ? {
    title: editing.title || "",
    titleEn: editing.titleEn || "",
    value: editing.value || "",
    sortOrder: editing.sortOrder != null ? String(editing.sortOrder) : "",
    isActive: editing.isActive !== false,
  } : EMPTY_KEY_FACT;

  const isFr = localeTab === "fr";

  return (
    <Formik
      initialValues={initial}
      validationSchema={schema}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        const payload = {
          title: values.title.trim(),
          titleEn: values.titleEn.trim() || null,
          value: values.value.trim(),
          sortOrder: values.sortOrder !== "" ? Number(values.sortOrder) : undefined,
          isActive: values.isActive,
        };
        try {
          if (editing) await apiPut(`/key-facts/${editing.id}`, payload);
          else await apiPost("/key-facts", payload);
          toast.success(t.common.saveSuccess);
          onSaved();
          onClose();
        } catch (e: any) {
          toast.error(e?.response?.data?.reason || e?.messageText || t.common.error);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, isSubmitting, setFieldValue }) => (
        <AdminFormDialog
          open={open}
          onClose={onClose}
          title={editing ? t.admin.editKeyFact : t.admin.newKeyFact}
          subtitle={t.admin.pageContent}
          testId="keyfact-modal"
          formId={FORM_ID}
          submitLabel={editing ? t.admin.save : t.admin.create}
          isSubmitting={isSubmitting}
          showLocaleTabs
          localeTab={localeTab}
          onLocaleChange={setLocaleTab}
        >
          <Form id={FORM_ID} className="grid gap-4 pb-4">
            <AdminField
              label={isFr ? t.admin.keyFactTitle : t.admin.keyFactTitleEn}
              required={isFr}
              error={isFr && touched.title ? errors.title : undefined}
            >
              <Field
                as={Input}
                name={isFr ? "title" : "titleEn"}
                data-testid="keyfact-title"
                placeholder="Ex: Etudiants sont formés chez nous"
              />
            </AdminField>
            <AdminField
              label={t.admin.keyFactValue}
              required
              error={touched.value ? errors.value : undefined}
            >
              <Field
                as={Input}
                name="value"
                data-testid="keyfact-value"
                placeholder="Ex: +10 000"
              />
            </AdminField>
            <div className="grid grid-cols-2 gap-4">
              <AdminField label={t.admin.keyFactOrder}>
                <Field as={Input} id="keyfact-order" name="sortOrder" type="number" min={0} />
              </AdminField>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={values.isActive}
                    onChange={(e) => setFieldValue("isActive", e.target.checked)}
                  />
                  {values.isActive ? t.admin.active : t.admin.inactive}
                </label>
              </div>
            </div>
          </Form>
        </AdminFormDialog>
      )}
    </Formik>
  );
};
